// One person reference, one address (SEM-6329 / LINK_INVENTORY B).
//
// 15 screens print an employee's name as dead text. Each could wrap its own
// RouterLink, and then each would decide separately what an absent id means,
// what the affordance looks like, and where a person lives. This component is
// the single answer, so a later route change is one edit.
//
// It does NOT ask whether the caller may see the person. The link's existence
// is not an authorization decision — the refusal comes from the server when
// the detail is read (SEM-6329 invariant). A permission-shaped link would need
// the caller's scope resolved on every row, and would silently hide people the
// server would in fact return.
//
// The address is the READ route, not `:id/edit`. The edit surface is
// admin-scoped; pointing a name at it turns "who is this?" into a permission
// question — the very defect this replaces.

import { EntityLink } from "./entity-link";

export function personPath(employeeId: string, listQuery = ""): string {
  return `/people/${employeeId}${listQuery === "" ? "" : `?${listQuery}`}`;
}

export interface PersonLinkProps {
  readonly employeeId: string | null | undefined;
  readonly children: React.ReactNode;
  readonly className?: string;
  /**
   * The filtered list this link is leaving, as a query string. The person page
   * hands it back on its return link, so a filtered list is still filtered when
   * the reader comes back (SEM-6350). Only a list screen has one to pass.
   */
  readonly listQuery?: string;
}

export function PersonLink({
  employeeId,
  children,
  className,
  listQuery,
}: PersonLinkProps): React.ReactElement {
  return (
    <EntityLink
      entityId={employeeId}
      toPath={(id): string => personPath(id, listQuery)}
      {...(className !== undefined && { className })}
    >
      {children}
    </EntityLink>
  );
}
